import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushNotificationToggle() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState("default");

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      checkExistingSubscription();
    }
  }, []);

  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      }
    } catch (e) {
      console.error("Error checking subscription:", e);
    }
  };

  const subscribe = async () => {
    setIsLoading(true);
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const res = await fetch(`${API}/admin/push/vapid-key`);
      const { public_key } = await res.json();

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error("Notifications refusées par le navigateur");
        setIsLoading(false);
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key)
      });

      // Send subscription to backend
      const subJson = subscription.toJSON();
      await fetch(`${API}/admin/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys
        })
      });

      setIsSubscribed(true);
      toast.success("Notifications activées ! Vous recevrez une alerte à chaque nouveau lead.");
    } catch (e) {
      console.error("Subscription error:", e);
      toast.error("Erreur lors de l'activation des notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          const subJson = subscription.toJSON();
          await fetch(`${API}/admin/push/unsubscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              endpoint: subJson.endpoint,
              keys: subJson.keys
            })
          });
          await subscription.unsubscribe();
        }
      }
      setIsSubscribed(false);
      toast.success("Notifications désactivées");
    } catch (e) {
      console.error("Unsubscribe error:", e);
      toast.error("Erreur lors de la désactivation");
    } finally {
      setIsLoading(false);
    }
  };

  const testNotification = async () => {
    try {
      await fetch(`${API}/admin/push/test`, { method: 'POST' });
      toast.success("Notification test envoyée !");
    } catch {
      toast.error("Erreur lors du test");
    }
  };

  if (!isSupported) return null;

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/30" title="Notifications bloquées dans les paramètres du navigateur">
        <BellOff className="w-3.5 h-3.5" />
        Notifs bloquées
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={isSubscribed ? unsubscribe : subscribe}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
          isSubscribed
            ? 'bg-[#EFFF00]/20 text-[#EFFF00] border border-[#EFFF00]/30'
            : 'bg-[#1a1a1a] text-[#666] border border-[#262626] hover:text-[#A3A3A3]'
        }`}
        data-testid="push-notification-toggle"
        title={isSubscribed ? "Notifications push activées" : "Activer les notifications push"}
      >
        {isLoading ? (
          <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isSubscribed ? (
          <BellRing className="w-3.5 h-3.5" />
        ) : (
          <Bell className="w-3.5 h-3.5" />
        )}
        {isSubscribed ? 'Notifs ON' : 'Notifs'}
      </button>
      {isSubscribed && (
        <button
          onClick={testNotification}
          className="px-2 py-2 rounded-lg text-xs text-[#666] hover:text-[#A3A3A3] transition-colors"
          title="Envoyer une notification test"
          data-testid="test-push-btn"
        >
          Test
        </button>
      )}
    </div>
  );
}
